import { getApiUrl } from '../config/api';
import { isTransientFailure, isTransientHttpStatus } from './availability';
import { prepareAvatarUpload } from '../platform/prepare-avatar';
import { readApiErrorMessage } from '../platform/prepare-avatar-core';

export const API_URL = getApiUrl();

export class ApiAuthError extends Error {
  constructor(message = 'Authentication required') {
    super(message);
    this.name = 'ApiAuthError';
  }
}

let unauthorizedHandler: (() => void) | undefined;
const availabilityListeners = new Set<(available: boolean) => void>();
let apiAvailable = true;

export function setUnauthorizedHandler(handler?: () => void) {
  unauthorizedHandler = handler;
}

export function subscribeApiAvailability(listener: (available: boolean) => void) {
  availabilityListeners.add(listener);
  listener(apiAvailable);
  return () => {
    availabilityListeners.delete(listener);
  };
}

export function getApiAvailable() {
  return apiAvailable;
}

function notifyApiAvailability(available: boolean) {
  if (apiAvailable === available) {
    return;
  }
  apiAvailable = available;
  availabilityListeners.forEach(listener => listener(available));
}

function trackResponse(response: Response) {
  if (response.status === 401) {
    return;
  }
  if (isTransientHttpStatus(response.status)) {
    notifyApiAvailability(false);
    return;
  }
  if (response.ok) {
    notifyApiAvailability(true);
  }
}

function trackNetworkError(error: unknown) {
  if (error instanceof ApiAuthError) {
    return;
  }
  if (isTransientFailure(error)) {
    notifyApiAvailability(false);
  }
}

function notifyUnauthorized(response: Response) {
  if (response.status === 401) {
    unauthorizedHandler?.();
    throw new ApiAuthError();
  }
}

export async function checkApiHealth() {
  try {
    const response = await fetch(`${API_URL}/health`, { cache: 'no-store' });
    trackResponse(response);
    return response.ok;
  } catch (error) {
    trackNetworkError(error);
    return false;
  }
}

export async function apiPatch(path: string, body: unknown, token?: string) {
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    trackResponse(response);
    notifyUnauthorized(response);
    return response.ok ? response.json() : null;
  } catch (error) {
    trackNetworkError(error);
    if (error instanceof ApiAuthError) {
      throw error;
    }
    return null;
  }
}

export async function apiPost(path: string, body: unknown, token?: string) {
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    trackResponse(response);
    notifyUnauthorized(response);
    return response.ok ? response.json() : null;
  } catch (error) {
    trackNetworkError(error);
    if (error instanceof ApiAuthError) {
      throw error;
    }
    return null;
  }
}

export async function apiGet(path: string, token?: string) {
  try {
    const response = await fetch(`${API_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    trackResponse(response);
    notifyUnauthorized(response);

    if (!response.ok) {
      throw new Error(`GET ${path} failed`);
    }

    return response.json();
  } catch (error) {
    trackNetworkError(error);
    throw error;
  }
}

export async function authenticate(path: string, body: object): Promise<{ token?: string; error?: string }> {
  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  const timeout = controller ? setTimeout(() => controller.abort(), 8000) : null;
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(controller ? { signal: controller.signal } : {}),
    });
    trackResponse(response);
    if (!response.ok) {
      let message = 'Request failed';
      try {
        const payload = await response.json() as { message?: string | string[] };
        if (Array.isArray(payload.message)) {
          message = payload.message.join(', ');
        } else if (typeof payload.message === 'string') {
          message = payload.message;
        }
      } catch {
        // ignore parse errors
      }
      return { error: message };
    }
    return response.json() as Promise<{ token: string }>;
  } catch (error) {
    trackNetworkError(error);
    return { error: 'Network request failed' };
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

export async function fetchBootstrap(token?: string) {
  try {
    const response = await fetch(`${API_URL}/bootstrap`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    trackResponse(response);
    notifyUnauthorized(response);

    if (!response.ok) {
      throw new Error('Failed to load bootstrap data');
    }

    return response.json();
  } catch (error) {
    trackNetworkError(error);
    throw error;
  }
}

async function appendAvatarToFormData(formData: FormData, file: { uri: string; type: string; name: string; file?: Blob }) {
  const prepared = await prepareAvatarUpload(file);
  if (prepared.file) {
    if (typeof File === 'function') {
      formData.append('avatar', new File([prepared.file], prepared.name, { type: prepared.type }));
    } else {
      formData.append('avatar', prepared.file, prepared.name);
    }
    return;
  }
  if (prepared.uri.startsWith('blob:') || prepared.uri.startsWith('data:')) {
    const blob = await fetch(prepared.uri).then(response => response.blob());
    if (typeof File === 'function') {
      formData.append('avatar', new File([blob], prepared.name, { type: prepared.type || blob.type || 'image/jpeg' }));
    } else {
      formData.append('avatar', blob, prepared.name);
    }
    return;
  }
  formData.append('avatar', {
    uri: prepared.uri,
    type: prepared.type,
    name: prepared.name,
  } as unknown as Blob);
}

export type AvatarUploadResult = {
  photoUrl?: string | null;
  error?: string;
};

export async function apiUploadAvatar(
  token: string,
  file: { uri: string; type: string; name: string; file?: Blob },
): Promise<AvatarUploadResult> {
  const formData = new FormData();
  try {
    await appendAvatarToFormData(formData, file);
  } catch (error) {
    if (error instanceof Error && error.message === 'PHOTO_TOO_LARGE') {
      return { error: 'PHOTO_TOO_LARGE' };
    }
    return { error: error instanceof Error ? error.message : 'UPLOAD_FAILED' };
  }

  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  const timeout = controller ? setTimeout(() => controller.abort(), 60_000) : null;

  try {
    const response = await fetch(`${API_URL}/users/me/avatar`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      signal: controller?.signal,
    });

    trackResponse(response);
    notifyUnauthorized(response);

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      return { error: readApiErrorMessage(body, 'UPLOAD_FAILED') };
    }

    return response.json() as Promise<{ photoUrl: string | null }>;
  } catch (error) {
    trackNetworkError(error);
    if (error instanceof ApiAuthError) {
      throw error;
    }
    return { error: 'UPLOAD_FAILED' };
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}
